/**
 * @module components/aiLayer
 *
 * The AI capability layer section: the six-layer stack, the "AI dots" spread
 * across the estate, the role-based agentic workforce, and the guardrails that
 * cut vertically through all of it.
 *
 * Each part pairs a data-driven list with an SVG scene, and the motion is
 * restrained: draw-on strokes, a slow orbit, and staggered entrance — nothing
 * that competes with the reading.
 */

import { gsap, EASE, MEDIA, createMotionContext, ScrollTrigger } from "../core/gsap-setup.js";
import { qs, qsa } from "../core/dom.js";
import { watchReveal } from "../animations/scrollReveal.js";
import {
  AI_STACK,
  AI_DOTS,
  AGENT_ROLES,
  AGENT_LOOP,
  AI_GUARDRAILS,
  AI_SUMMARY,
} from "../data/aiLayer.js";
import { icon } from "../svg/icons.js";
import { aiStack, dotConstellation, agentLoop } from "../svg/scenes.js";

/**
 * Render one stack layer card.
 *
 * @param {import("../data/aiLayer.js").AiStackLayer} layer
 * @returns {string} Markup.
 */
function layerMarkup(layer) {
  return `
    <li class="ai-layer-card" data-ai-stack-layer="${layer.id}" style="--layer-color:${layer.accent}">
      <span class="ai-layer-card__index" aria-hidden="true">${layer.index}</span>
      <div>
        <p class="ai-layer-card__name">${icon(layer.glyph, { size: 17 })}${layer.name}</p>
        <p class="ai-layer-card__body">${layer.body}</p>
        <ul class="ai-layer-card__components">
          ${layer.components.map((component) => `<li>${component}</li>`).join("")}
        </ul>
      </div>
    </li>`;
}

/**
 * Render one AI dot card.
 *
 * @param {import("../data/aiLayer.js").AiDot} dot
 * @returns {string} Markup.
 */
function dotMarkup(dot) {
  return `
    <li class="ai-dot" data-ai-dot="${dot.id}">
      <p class="ai-dot__where">${dot.where}</p>
      <p class="ai-dot__label">${icon(dot.glyph, { size: 15 })}${dot.label}</p>
      <p class="ai-dot__body">${dot.body}</p>
    </li>`;
}

/**
 * Render one agent card.
 *
 * @param {import("../data/aiLayer.js").AgentRole} agent
 * @returns {string} Markup.
 */
function agentMarkup(agent) {
  const phaseName = agent.phaseId.replace(/-/g, " ");
  return `
    <li class="agent-card" data-agent="${agent.id}">
      <div class="agent-card__head">
        <h3 class="agent-card__name">${agent.name}</h3>
        <span class="agent-card__phase">Supports: ${phaseName}</span>
      </div>
      <p class="agent-card__mandate">${agent.mandate}</p>
      <dl class="agent-card__meta">
        <div>
          <dt>Tools</dt>
          <dd>${agent.tools.join(" · ")}</dd>
        </div>
        <div class="agent-card__boundary">
          ${icon("lock", { size: 14 })}
          <div>
            <dt>Boundary</dt>
            <dd>${agent.boundary}</dd>
          </div>
        </div>
      </dl>
    </li>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initAiLayer() {
  const root = qs("[data-ai-layer]");
  if (!root) return () => {};

  /* ---------------------------------------------------------------------- */
  /* Static markup from data                                                 */
  /* ---------------------------------------------------------------------- */

  const eyebrow = qs("[data-ai-eyebrow]", root);
  const title = qs("[data-ai-title]", root);
  const lede = qs("[data-ai-lede]", root);
  if (eyebrow) eyebrow.textContent = AI_SUMMARY.eyebrow;
  if (title) title.textContent = AI_SUMMARY.title;
  if (lede) lede.textContent = AI_SUMMARY.lede;

  const stackScene = qs("[data-ai-stack-scene]", root);
  if (stackScene) {
    stackScene.innerHTML = aiStack({
      layers: AI_STACK.map((layer) => ({ name: layer.name, short: layer.short, accent: layer.accent })),
      governanceAccent: "#e0b584",
      governanceLabel: "Governance",
    });
  }

  const layerList = qs("[data-ai-layers]", root);
  if (layerList) layerList.innerHTML = AI_STACK.map(layerMarkup).join("");

  const dotsScene = qs("[data-ai-dots-scene]", root);
  if (dotsScene) dotsScene.innerHTML = dotConstellation({ dots: AI_DOTS });

  const dotsList = qs("[data-ai-dots]", root);
  if (dotsList) dotsList.innerHTML = AI_DOTS.map(dotMarkup).join("");

  const agentsScene = qs("[data-agents-scene]", root);
  if (agentsScene) {
    agentsScene.innerHTML = agentLoop({
      steps: AGENT_LOOP,
      agents: AGENT_ROLES.map((agent) => ({ id: agent.id, name: agent.name, glyph: "spark" })),
      accent: "#5fd4c4",
      secondary: "#d8a657",
    });
  }

  const agentList = qs("[data-agents]", root);
  if (agentList) agentList.innerHTML = AGENT_ROLES.map(agentMarkup).join("");

  const guardrailList = qs("[data-guardrails]", root);
  if (guardrailList) {
    guardrailList.innerHTML = AI_GUARDRAILS.map(
      (guardrail) => `
      <li class="guardrail" data-guardrail="${guardrail.id}">
        <p class="guardrail__name">${icon(guardrail.glyph, { size: 17 })}${guardrail.name}</p>
        <p class="guardrail__body">${guardrail.body}</p>
      </li>`
    ).join("");
  }

  /* ---------------------------------------------------------------------- */
  /* Motion                                                                  */
  /* ---------------------------------------------------------------------- */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const cleanup = [];

    /* --- Stack: layers rise into place, governance spine draws ---------- */

    if (stackScene) {
      const planes = qsa(".stack-layer", stackScene);
      const spine = qsa(".stack-spine line", stackScene);

      if (planes.length) {
        cleanup.push(
          watchReveal(planes, {
            from: { autoAlpha: 0, y: 26 },
            duration: 0.8,
            stagger: { each: 0.09, from: "end" },
            start: "top 82%",
          })
        );
      }

      if (spine.length) {
        cleanup.push(
          watchReveal(spine, {
            from: { drawSVG: "0%" },
            to: { drawSVG: "100%" },
            duration: 1.4,
            ease: "power2.inOut",
            stagger: 0.06,
            start: "top 82%",
          })
        );
      }
    }

    /* --- AI dots: stagger in, then drift -------------------------------- */

    if (dotsScene) {
      const nodes = qsa(".dot-node", dotsScene);
      const links = qsa(".dot-link", dotsScene);

      if (links.length) {
        cleanup.push(
          watchReveal(links, {
            from: { drawSVG: "0%" },
            to: { drawSVG: "100%" },
            duration: 1.6,
            ease: "power1.inOut",
            stagger: 0.05,
            start: "top 84%",
          })
        );
      }

      if (nodes.length) {
        cleanup.push(
          watchReveal(nodes, {
            from: { autoAlpha: 0, scale: 0.4 },
            to: { autoAlpha: 1, scale: 1 },
            duration: 0.7,
            ease: "back.out(1.5)",
            stagger: { each: 0.06, from: "random" },
            start: "top 84%",
          })
        );

        // A barely-there float so the constellation feels alive.
        const float = nodes.map((node, index) =>
          gsap.to(node, {
            y: index % 2 === 0 ? -5 : 5,
            duration: 3.2 + (index % 4) * 0.6,
            ease: EASE.float,
            repeat: -1,
            yoyo: true,
            delay: 0.4 + index * 0.12,
          })
        );

        cleanup.push(() => float.forEach((tween) => tween.kill()));
      }
    }

    /* --- Agent loop: rotating connectors, pulsing steps ------------------ */

    if (agentsScene) {
      const connectors = qs(".loop-connectors", agentsScene);
      const steps = qsa(".loop-step", agentsScene);
      const core = qs(".loop-core", agentsScene);
      const agents = qsa(".loop-agent", agentsScene);

      if (connectors) {
        cleanup.push(
          watchReveal(connectors, { from: { autoAlpha: 0 }, to: { autoAlpha: 1 }, duration: 1.2, start: "top 84%" })
        );
      }

      if (steps.length) {
        cleanup.push(
          watchReveal(steps, {
            from: { autoAlpha: 0, scale: 0.7 },
            to: { autoAlpha: 1, scale: 1 },
            duration: 0.6,
            ease: "back.out(1.6)",
            stagger: 0.1,
            start: "top 84%",
          })
        );
      }

      if (agents.length) {
        cleanup.push(watchReveal(agents, { from: { autoAlpha: 0 }, to: { autoAlpha: 1 }, duration: 0.9, stagger: 0.08, start: "top 82%" }));
      }

      if (core) {
        const pulse = gsap.to(core, {
          scale: 1.05,
          duration: 3.6,
          ease: EASE.float,
          repeat: -1,
          yoyo: true,
          transformOrigin: "50% 50%",
        });
        cleanup.push(() => pulse.kill());
      }
    }

    /* --- Content entrance ------------------------------------------------ */

    const cards = qsa(".ai-layer-card, .ai-dot, .agent-card, .guardrail", root);
    if (cards.length) {
      cleanup.push(watchReveal(cards, { from: { autoAlpha: 0, y: 18 }, duration: 0.6, stagger: 0.03, start: "top 80%" }));
    }

    return () => cleanup.forEach((dispose) => dispose());
  });

  // Positions shift once the injected scenes have real dimensions.
  ScrollTrigger.refresh();

  return () => mm.revert();
}
